
import React, { Component, ErrorInfo } from "react";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private isChunkLoadError(error: Error | null): boolean {
    if (!error) return false;
    const msg = error.message || '';
    return (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Importing a module script failed') ||
      msg.includes('Unable to preload CSS') ||
      error.name === 'ChunkLoadError'
    );
  }

  public render() {
    if (this.state.hasError) {
      const isChunk = this.isChunkLoadError(this.state.error);
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center p-8 max-w-md">
            <h1 className="text-2xl font-bold text-destructive mb-4">
              {isChunk ? 'Page needs to reload' : 'Something went wrong'}
            </h1>
            <p className="text-muted-foreground mb-6">
              {isChunk
                ? 'A newer version of the app is available. Reloading will fix this.'
                : (this.state.error?.message || 'An unexpected error occurred.')}
            </p>
            <button
              className="bg-primary text-primary-foreground px-6 py-2 rounded font-medium"
              onClick={() => window.location.reload()}
            >
              {isChunk ? 'Reload Now' : 'Reload Page'}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
